import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { LogOut, Plus } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import korzaLogo from '@/assets/korza_logo_black_blue.png';
import korzaLogoWhite from '@/assets/korza_logo_white.png';
import torusStill from '@/assets/brand/torus.webp';
import { Button } from '@/components/ui/button';
import { LanguageSelector } from '@/components/language-selector';
import { GrainOverlay } from '@/components/brand/grain-overlay';
import { LongArrow } from '@/components/brand/long-arrow';
import {
  KorzaTorus,
  type KorzaTorusControls,
} from '@/components/brand/korza-torus/korza-torus';
import type { TenantDto } from '@/generated/korza-api-client/types.gen';
import { useIsMobile } from '@/hooks/use-mobile';
import { useI18nContext } from '@/i18n/i18n-react';
import { StoreIcon } from '@/pages/select-tenant/store-icon';
import type { StorePickerProps } from '@/pages/select-tenant/store-picker-props';

// Each card gets its own take on the brand book's grainy blue gradient so a
// row of stores doesn't read as copies of one tile.
const CARD_GLOWS = [
  'radial-gradient(60% 50% at 80% 12%, rgba(21,93,252,0.95), transparent 72%), radial-gradient(55% 45% at 15% 95%, rgba(150,185,255,0.6), transparent 70%), linear-gradient(165deg, #0B348C 0%, #15191E 78%)',
  'radial-gradient(55% 50% at 20% 10%, rgba(21,93,252,0.9), transparent 72%), radial-gradient(60% 45% at 90% 90%, rgba(120,160,255,0.55), transparent 70%), linear-gradient(200deg, #15191E 10%, #0B348C 100%)',
  'radial-gradient(65% 45% at 50% 0%, rgba(21,93,252,0.85), transparent 72%), radial-gradient(50% 50% at 100% 70%, rgba(170,200,255,0.45), transparent 70%), linear-gradient(180deg, #0B348C 0%, #15191E 85%)',
];

const OVERLAY_GRADIENT =
  'radial-gradient(50% 45% at 70% 25%, rgba(21,93,252,0.95), transparent 70%), radial-gradient(45% 40% at 20% 85%, rgba(140,175,255,0.55), transparent 70%), linear-gradient(160deg, #0B348C, #15191E 70%)';

// The ring surges first, then the store takes over the screen, then we
// navigate — long enough to land, short enough not to feel like a wait.
const SURGE_MS = 350;
const OVERLAY_MS = 750;

const STAGE_MASK =
  'linear-gradient(180deg, transparent 0, #000 10%, #000 88%, transparent 100%)';

/**
 * Store choice for merchants (no full access): usually one or two stores, so
 * each gets a large card with its app icon front and center, over the brand
 * book's light ground and live 3D torus.
 */
export function MerchantStorePicker({
  tenants,
  creationRequests,
  email,
  onSelect,
  onLogout,
}: StorePickerProps) {
  const { LL } = useI18nContext();
  const isMobile = useIsMobile();
  const reduceMotion = useReducedMotion();
  const torusRef = useRef<KorzaTorusControls>(null);
  const timers = useRef<number[]>([]);
  // A ref, not just state: a second click can land before the re-render
  // that disables the cards, and must not schedule a second navigation.
  const choseRef = useRef(false);
  const [chosen, setChosen] = useState<TenantDto | null>(null);
  const [showTakeover, setShowTakeover] = useState(false);

  useEffect(() => {
    const pending = timers.current;
    return () => pending.forEach((id) => window.clearTimeout(id));
  }, []);

  const enter = (tenant: TenantDto) => {
    if (choseRef.current) return;
    choseRef.current = true;
    setChosen(tenant);
    if (reduceMotion) {
      onSelect(tenant.slug);
      return;
    }
    torusRef.current?.boost();
    timers.current.push(
      window.setTimeout(() => setShowTakeover(true), SURGE_MS),
      window.setTimeout(() => onSelect(tenant.slug), SURGE_MS + OVERLAY_MS)
    );
  };

  const count = String(tenants.length + creationRequests.length).padStart(
    2,
    '0'
  );
  const isEmpty = tenants.length === 0 && creationRequests.length === 0;
  const torusFallback = (
    <img
      src={torusStill}
      alt=""
      aria-hidden="true"
      className="pointer-events-none absolute right-[-140px] top-[30px] w-[52%] max-w-[820px] max-md:right-[-24px] max-md:top-0 max-md:h-full max-md:w-auto"
    />
  );

  return (
    <div className="relative flex min-h-dvh w-full flex-col overflow-hidden bg-[#F9F9F9] text-[#15191E]">
      {!isMobile && (
        <KorzaTorus
          ref={torusRef}
          className="pointer-events-none absolute right-[-140px] top-[30px] aspect-[820/760] w-[52%] max-w-[820px]"
          fallback={torusFallback}
        />
      )}

      <header className="relative z-10 flex h-16 shrink-0 items-center gap-2 pl-6 pr-3 md:h-20 md:gap-4 md:border-b md:border-[#15191E]/[.07] md:px-20">
        <img src={korzaLogo} alt="Korza" className="h-4 w-auto md:h-5" />
        <div className="flex-1" />
        {email && (
          <span className="hidden text-sm text-[#4B5361] md:inline">
            {email}
          </span>
        )}
        <LanguageSelector className="w-auto border-none bg-transparent shadow-none hover:bg-[#15191E]/[.06]" />
        <Button
          type="button"
          variant="ghost"
          onClick={onLogout}
          className="min-h-11 gap-2 rounded-[10px] px-3 hover:bg-[#15191E]/[.06]"
        >
          <LogOut className="size-[18px]" strokeWidth={1.5} />
          <span className="max-md:sr-only">
            {LL.selectTenant.logoutButton()}
          </span>
        </Button>
      </header>

      <main className="relative z-[1] flex w-full max-w-[1440px] flex-1 flex-col gap-6 px-6 md:gap-14 md:px-20 md:pb-14 md:pt-[72px]">
        {isMobile && (
          <div
            className="relative -mx-6 -mb-2.5 -mt-3 h-[212px] shrink-0"
            style={{ maskImage: STAGE_MASK, WebkitMaskImage: STAGE_MASK }}
          >
            <KorzaTorus
              ref={torusRef}
              zoom={1.22}
              offsetX={1.2}
              className="absolute inset-0 size-full"
              fallback={torusFallback}
            />
          </div>
        )}

        <div className="flex max-w-[640px] flex-col gap-4 md:gap-6">
          <div className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#155DFC] md:gap-3.5 md:text-xs">
            <span>{LL.selectTenant.merchant.eyebrow()}</span>
            <span
              aria-hidden="true"
              className="h-px w-10 bg-[#155DFC]/35 md:w-16"
            />
            <span>{count}</span>
          </div>
          <h1 className="text-[40px] font-medium leading-[1.05] tracking-[-0.035em] md:text-[68px] md:leading-[1.02]">
            {LL.selectTenant.merchant.greeting()}
            <br />
            <span className="text-[#155DFC]">
              {LL.selectTenant.merchant.greetingAccent()}
            </span>
          </h1>
          <p className="max-w-[460px] text-base leading-normal text-[#4B5361] md:text-[19px]">
            {LL.selectTenant.merchant.subtitle()}
          </p>
        </div>

        <div className="flex flex-col gap-3.5 pb-6 md:flex-row md:flex-wrap md:gap-6 md:pb-0">
          {tenants.map((tenant, index) => (
            <StoreCard
              key={tenant.id}
              tenant={tenant}
              number={String(index + 1).padStart(2, '0')}
              glow={CARD_GLOWS[index % CARD_GLOWS.length]}
              disabled={chosen !== null}
              onEnter={() => enter(tenant)}
            />
          ))}
          {creationRequests.map((request) => (
            <DraftCard
              key={request.slug}
              slug={request.slug}
              storeName={request.storeName ?? LL.selectTenant.noName()}
            />
          ))}
          {isEmpty && (
            <p className="py-8 text-[#4B5361]">
              {LL.selectTenant.noAppsFound()}
            </p>
          )}
        </div>
      </main>

      {email && (
        <footer className="relative z-[1] border-t border-[#15191E]/[.07] truncate px-6 pb-7 pl-24 pt-4 text-right text-[13px] text-[#4B5361] md:hidden">
          {email}
        </footer>
      )}

      <AnimatePresence>
        {showTakeover && chosen && (
          <motion.div
            role="status"
            aria-live="polite"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex flex-col items-center justify-center overflow-hidden bg-[#15191E] text-white"
          >
            <div
              aria-hidden="true"
              className="absolute -inset-[20%]"
              style={{ background: OVERLAY_GRADIENT }}
            />
            <GrainOverlay />
            <motion.div
              initial={{ opacity: 0, scale: 0.7, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 260, damping: 20 }}
              className="relative flex flex-col items-center gap-7 px-6 text-center"
            >
              <StoreIcon
                src={chosen.iconUrl}
                className="size-[116px] rounded-[29px] shadow-[0_40px_80px_-20px_rgba(0,0,0,0.6)] ring-1 ring-white/[.16] md:size-[136px] md:rounded-[34px]"
              />
              <div className="flex flex-col items-center gap-2.5">
                <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75 md:text-xs">
                  {LL.selectTenant.merchant.opening()}
                </span>
                <span className="text-[32px] font-medium tracking-[-0.03em] md:text-[44px]">
                  {chosen.name}
                </span>
              </div>
              <div className="h-px w-[180px] bg-white/[.18] md:w-60">
                <motion.div
                  className="h-px bg-white"
                  initial={{ width: 0 }}
                  animate={{ width: '100%' }}
                  transition={{
                    duration: OVERLAY_MS / 1000,
                    ease: [0.6, 0, 0.2, 1],
                  }}
                />
              </div>
            </motion.div>
            <img
              src={korzaLogoWhite}
              alt="Korza"
              className="absolute bottom-10 h-3.5 w-auto opacity-85 md:h-4"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function StoreCard({
  tenant,
  number,
  glow,
  disabled,
  onEnter,
}: {
  tenant: TenantDto;
  number: string;
  glow: string;
  disabled: boolean;
  onEnter: () => void;
}) {
  const { LL } = useI18nContext();

  return (
    <button
      type="button"
      onClick={onEnter}
      disabled={disabled}
      aria-label={LL.selectTenant.merchant.enterStore({ name: tenant.name })}
      className="group relative flex h-[168px] w-full shrink-0 flex-col overflow-hidden rounded-[22px] bg-[#15191E] text-left text-white shadow-[0_24px_40px_-24px_rgba(11,52,140,0.55)] outline-none transition-[transform,box-shadow] duration-500 ease-[cubic-bezier(.2,.8,.2,1)] focus-visible:ring-2 focus-visible:ring-[#155DFC] focus-visible:ring-offset-4 focus-visible:ring-offset-[#F9F9F9] active:scale-[.98] disabled:cursor-default motion-reduce:transition-none md:h-[404px] md:w-[320px] md:rounded-3xl md:active:scale-100 md:enabled:hover:-translate-y-2 md:enabled:hover:shadow-[0_40px_80px_-24px_rgba(11,52,140,0.55)]"
    >
      <span
        aria-hidden="true"
        className="absolute -inset-[10%] transition-transform duration-1000 ease-[cubic-bezier(.2,.8,.2,1)] group-hover:-translate-x-[3%] group-hover:translate-y-[3%] group-hover:scale-110 motion-reduce:transition-none"
        style={{ background: glow }}
      />
      <GrainOverlay />

      <span className="relative grid h-[38px] w-full shrink-0 grid-cols-2 border-b border-white/[.12] text-[10px] font-semibold uppercase tracking-[0.16em] text-white/[.88] md:h-12 md:text-[10.5px]">
        <span className="flex items-center border-r border-white/[.12] pl-[18px] md:pl-[22px]">
          {LL.selectTenant.merchant.storeNumber({ number })}
        </span>
        <span className="flex min-w-0 items-center truncate pl-[18px] md:pl-[22px]">
          {tenant.ecommercePlatformReadableName}
        </span>
      </span>

      <span className="relative flex w-full flex-1 items-center gap-[18px] px-[18px] md:flex-col md:justify-center md:gap-[22px] md:px-6">
        <StoreIcon
          src={tenant.iconUrl}
          className="size-[84px] rounded-[21px] shadow-[0_16px_32px_-10px_rgba(0,0,0,0.5)] ring-1 ring-white/[.14] transition-transform duration-500 motion-reduce:transition-none md:size-28 md:rounded-[28px] md:group-enabled:group-hover:-translate-y-1 md:group-enabled:group-hover:scale-[1.04]"
        />
        <span className="flex min-w-0 flex-1 flex-col gap-1 md:w-full md:flex-none md:items-center md:gap-1.5">
          <span className="w-full truncate text-[21px] font-medium tracking-[-0.02em] md:text-center md:text-[26px]">
            {tenant.name}
          </span>
          <span className="w-full truncate text-[13px] text-white/[.72] md:text-center">
            {tenant.storeWebsiteDomain || tenant.slug}
          </span>
        </span>
        <LongArrow className="shrink-0 md:hidden" lineClassName="w-6" />
      </span>

      <span className="relative hidden h-16 w-full shrink-0 items-center justify-between border-t border-white/[.12] px-[22px] md:flex">
        <span className="text-sm font-semibold">
          {LL.selectTenant.merchant.enter()}
        </span>
        <LongArrow lineClassName="group-enabled:group-hover:w-[76px]" />
      </span>
    </button>
  );
}

function DraftCard({ slug, storeName }: { slug: string; storeName: string }) {
  const { LL } = useI18nContext();

  return (
    <Link
      to={`/onboarding/${slug}`}
      className="group flex h-[104px] w-full shrink-0 items-center gap-4 rounded-[22px] border-[1.5px] border-dashed border-[#0B348C]/[.28] bg-white/70 px-[18px] text-[#15191E] outline-none transition-colors hover:border-[#155DFC] hover:bg-white/90 focus-visible:ring-2 focus-visible:ring-[#155DFC] focus-visible:ring-offset-4 focus-visible:ring-offset-[#F9F9F9] md:h-[404px] md:w-[320px] md:flex-col md:items-stretch md:gap-0 md:rounded-3xl md:bg-white/60 md:px-0 md:backdrop-blur-md"
    >
      <span className="hidden h-12 items-center border-b border-[#0B348C]/[.12] pl-[22px] text-[10.5px] font-semibold uppercase tracking-[0.16em] text-[#155DFC] md:flex">
        {LL.selectTenant.merchant.draftLabel()}
      </span>
      <span className="flex size-16 shrink-0 items-center justify-center rounded-[17px] border-[1.5px] border-dashed border-[#155DFC]/45 text-[#155DFC] md:hidden">
        <Plus className="size-6" strokeWidth={1.5} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-1 md:items-center md:justify-center md:gap-[22px] md:px-7 md:text-center">
        <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#155DFC] md:hidden">
          {LL.selectTenant.merchant.draftLabel()}
        </span>
        <span className="hidden size-28 items-center justify-center rounded-[28px] border-[1.5px] border-dashed border-[#155DFC]/45 text-[#155DFC] md:flex">
          <Plus className="size-[34px]" strokeWidth={1.5} />
        </span>
        <span className="flex flex-col gap-1 md:gap-2">
          <span className="truncate text-lg font-medium tracking-[-0.02em] md:text-2xl">
            {storeName}
          </span>
          <span className="text-[13px] text-[#4B5361] md:hidden">
            {LL.selectTenant.merchant.continueCreation()}
          </span>
          <span className="hidden text-sm leading-normal text-[#4B5361] md:inline">
            {LL.selectTenant.merchant.draftHint()}
          </span>
        </span>
      </span>
      <LongArrow
        className="shrink-0 text-[#155DFC] md:hidden"
        lineClassName="w-5"
      />
      <span className="hidden h-16 items-center justify-between border-t border-[#0B348C]/[.12] px-[22px] text-[#155DFC] md:flex">
        <span className="text-sm font-semibold">
          {LL.selectTenant.merchant.continueCreation()}
        </span>
        <LongArrow lineClassName="w-5 group-hover:w-10" />
      </span>
    </Link>
  );
}
